import { IPouchCreds, ICouchUser, IReplicateSubscription } from 'shared-types/index';
import UtilsService from 'external-services/utils-service';
import { cloneDeep } from 'lodash-es';
import DateUtilsService from 'external-services/date-utils-service';
import NetworkConnectionService from 'external-services//network-connection-service/index';
// @todo: These types were specific to Diary Mobile, but must be in shared-types. Jim to fix
import { appURLS } from 'appRoot/app.constants';
import { COUCH_ONLINE_DAYS_BACK, IReducedInfo } from './types';
import NetworkRequestsService from 'external-services/network-requests-service';
import { IAuthService } from 'external-services/diary-admin-service/types';
import { AxiosInstance } from 'axios';

const NS = 'PouchLoadService';

const logTag = `[Pouch DB - ${NS}] ==>`;

const api = NetworkRequestsService.getGlobalAPI();

class PouchLoadService {
  private dbLocal: PouchDB.Database;
  private dbOnline: PouchDB.Database;
  private getVenueTimezoneId: () => string;
  private attemptMaxCount: number = 100;

  private reducedInfo: IReducedInfo = null;

  setup(attemptMaxCount: number) {
    this.attemptMaxCount = attemptMaxCount;
  }

  setReducedInfo(reducedInfo: IReducedInfo): void {
    this.reducedInfo = reducedInfo;
  }

  /**
   * This is only for Unit test
   * @param dbLocal
   * @param dbOnline
   */
  _setupForUnitTest(dbLocal: any, dbOnline: any) {
    this.dbLocal = dbLocal;
    this.dbOnline = dbOnline;
  }

  init(getVenueTimezoneId: () => string): void {
    this.getVenueTimezoneId = getVenueTimezoneId;
  }

  getDbLocal(): PouchDB.Database {
    return this.dbLocal;
  }

  getDbOnline(): PouchDB.Database {
    return this.dbOnline;
  }

  clearDbOnline(): void {
    this.dbOnline = null;
  }

  async getCouchUsers(venueId: number): Promise<ICouchUser> {

    const headersConfig: any = {
      cache: false
    };

    const response = await api.post(appURLS.API + '/restaurants/venues/' + venueId + '/couchusers', {}, headersConfig);
    return response.data;
  }

  async createDbLocal(
    localPouchName: string,
    syncLogStack: string[],
    reducedInfo: IReducedInfo
  ): Promise<PouchDB.Database> {
    try {
      syncLogStack.push(
        `3. Starting PouchDb instance localPouchName: ${localPouchName}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
    } catch (err) {
      console.error(`3. Starting PouchDb -> log ${err}`);
    }
    this.reducedInfo = reducedInfo;

    let dbLocal: PouchDB.Database;
    try {
      dbLocal = this.createNewDbLocal(localPouchName);
    } catch (err) {
      console.log(NS, 'err', err);
      dbLocal = this.createNewDbLocal(localPouchName);
    }

    try {
      await dbLocal.info(); // check to make sure info is callable
    } catch (err) {
      try {
        console.log(
          logTag,
          `3.1. PouchDb error calling info() localPouchName: ${localPouchName}, reducedInfo: ${
            reducedInfo?.url || ''
          } >>`
        );
        syncLogStack.push(
          `3.1. PouchDb error calling info() localPouchName: ${localPouchName}, reducedInfo: ${
            reducedInfo?.url || ''
          } >>`
        );
      } catch (err) {
        console.error(`3.1. PouchDb error calling info()-> log ${err}`);
      }
      return null;
    }

    this.dbLocal = dbLocal;

    try {
      console.log(
        logTag,
        `3.1. PouchDb instance created localPouchName: ${localPouchName}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
      syncLogStack.push(
        `3.1. PouchDb instance created  localPouchName: ${localPouchName}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
    } catch (err) {
      console.error(`3.1. PouchDb instance created-> log ${err}`);
    }

    return dbLocal;
  }

  async createOnlineOnlyDataBase(
    fullInfo: IPouchCreds,
    syncLogStack: string[],
    getBearerToken: () => string,
    retryCount: number
  ): Promise<PouchDB.Database> {
    const MAX_RETRIES = 3;

    // Too many retries. Give up and resolve to false.
    if (retryCount >= MAX_RETRIES) {
      try {
        console.error(logTag, `7.9b. Failed onlineDB fullInfo: ${fullInfo?.url || ''} >>`);
        syncLogStack.push(`7.9b. Failed onlineDB fullInfo: ${fullInfo?.url || ''} >>`);
      } catch (err) {
        console.error(`7.9b. Failed onlineDB -> log ${err}`);
      }
      return null;
    }

    const options = {
      auth: {
        username: fullInfo.username,
        password: fullInfo.password,
      },
      ajax: {
        timeout: 300000,
        cache: true,
        headers: {
          'Authorization': getBearerToken()
        },
        withCredentials: false,
      },
    };

    const dbOnline = new PouchDB(fullInfo.url, options); // should we check for nulls instead ? than calling

    try {
      await dbOnline.info(); // this is when the info is actually called, its becoming null for some reason.
    } catch (err) {
      console.error(
        logTag,
        `Create onlineDB failed, but trying again. Attempt ${retryCount + 1}/${MAX_RETRIES}.`,
        err
      );
      try {
        console.error(logTag, `7.9a. Retrying onlineDB fullInfo: ${fullInfo?.url || ''} >>`, err);
        syncLogStack.push(`7.9a. Retrying onlineDB fullInfo: ${fullInfo?.url || ''} >>`);
      } catch (err) {
        console.error(`7.9a. Retrying onlineDB -> log ${err}`);
      }
      return this.createOnlineOnlyDataBase(fullInfo, syncLogStack, getBearerToken, retryCount + 1);
    }

    this.dbOnline = dbOnline;
    try {
      console.log(logTag, `7.1. Created onlineDB fullInfo: ${fullInfo?.url || ''} >>`);
      syncLogStack.push(`7.1. Created onlineDB fullInfo: ${fullInfo?.url || ''} >>`);
    } catch (err) {
      console.error(`7.1. Created onlineDB -> log ${err}`);
    }
    return dbOnline;
  }

  public getNewId(preParts: string[], simple = false, postParts?: string[]) {
    let parts = preParts.concat([UtilsService.generateRandomCode()]);

    if (!simple) {
      parts.push(new Date().getTime().toString());
    }

    if (postParts) {
      parts = parts.concat(postParts);
    }

    return parts.join('_');
  }

  /**
   * Bookings and booking changes are only stored in the local database for x days
   * @param {Date} date1 - ???
   * @param {Date} [date2] - ???
   */
  public selectDatabase(
    date1: string | Date = null,
    date2: string | Date = null
  ): PouchDB.Database {
    if (
      (date1 && !this.isDateInLocalDBRange(date1)) ||
      (date2 && !this.isDateInLocalDBRange(date1))
    ) {
      return this.getDbOnline();
    }
    return this.getDbLocal();
  }

  /**
   * Bookings and booking changes are only stored in the local database for x days
   */
  public isDateInLocalDBRange(date: string | Date): boolean {
    const timezoneId = this.getVenueTimezoneId();
    if (date) {
      let dateToCheck: Date;
      if (!(date instanceof Date)) {
        dateToCheck = new Date(date as string);
      } else {
        dateToCheck = cloneDeep(date);
      }

      dateToCheck.setHours(0, 0, 0, 0);
      const now = DateUtilsService.getVenueTime(timezoneId);
      now.setHours(0, 0, 0, 0);
      const msDiff: number = now.getTime() - dateToCheck.getTime();
      const daysOld: number = msDiff / (1000 * 60 * 60 * 24);
      if (daysOld > COUCH_ONLINE_DAYS_BACK) {
        console.log('Pouch load service:', 'Selected online pouch db.');
        return false;
      }
    }
    return true;
  }

  public checkDatabaseClosed(
    syncLogStack: string[],
    replicateSubscriptions: IReplicateSubscription
  ): Promise<any> {
    let attemptCount: number = 0;
    return new Promise((resolve, reject) => {
      console.log(logTag, '2. checking database closed  >>');
      syncLogStack.push('2. checking database closed  >>');

      // eslint-disable-next-line prefer-const
      let closeCheckInterval: NodeJS.Timeout;

      // Check all replicate are cencelled
      const doCheck = () => {
        if (
          (replicateSubscriptions.initialFrom == null ||
            replicateSubscriptions.initialFrom.cancelled) &&
          (replicateSubscriptions.liveTo == null || replicateSubscriptions.liveTo.cancelled) &&
          (replicateSubscriptions.liveFrom == null || replicateSubscriptions.liveFrom.cancelled)
        ) {
          // All replication are terminated, DB is succesfully closed
          clearInterval(closeCheckInterval);
          // console.log(logTag, 'All replications are closed');
          resolve(null);
        } else {
          ++attemptCount;
          if (attemptCount >= this.attemptMaxCount) {
            clearInterval(closeCheckInterval);
            reject('error, checking database closed');
          }
        }
      };

      // Endless check until verified
      closeCheckInterval = setInterval(doCheck, 500);

      // Start checking
      doCheck();
    });
  }

  /** Request close of replication and changes, need to resolve checkDatabaseClosed to guarantee DB is closed */
  public closeDatabase(
    syncLogStack: string[],
    replicateSubscriptions: IReplicateSubscription,
    syncCheckInterval: any
  ): void {
    console.log(logTag, '1. closing database  >>');
    syncLogStack.push('1. closing database  >>');
    // Stop recurrent check on sync changes
    clearTimeout(syncCheckInterval);

    // Cancel every running replication
    for (const key in replicateSubscriptions) {
      if (
        (replicateSubscriptions as any)[key] != null &&
        (replicateSubscriptions as any)[key].cancel
      ) {
        (replicateSubscriptions as any)[key].cancel();
      }
    }

    // Reset to initial status

    this.clearDbLocal();
    this.clearDbOnline();
  }

  public getDBOnlineReduced(): PouchDB.Database {
    const name = this.reducedInfo.url;
    const options = {
      auth: {
        username: this.reducedInfo.username,
        password: this.reducedInfo.password,
      },
      ajax: {
        timeout: 300000,
        cache: true,
        withCredentials: false,
      },
    };
    const p = new PouchDB(name, options);
    return p;
  }

  getDBOnlineReducedAsFirstOption(): PouchDB.Database {
    return NetworkConnectionService.isOnline() ? this.getDBOnlineReduced() : this.getDbLocal();
  }

  public selectOnlineDatabaseAsFirstOption(date: Date | string): PouchDB.Database {
    return NetworkConnectionService.isOnline()
      ? date && !this.isDateInLocalDBRange(date)
        ? this.getDbOnline()
        : this.getDBOnlineReduced()
      : this.getDbLocal();
  }

  private createNewDbLocal(localPouchName: string): PouchDB.Database {
    return new PouchDB(localPouchName, {
      size: 50, // WebSQL only for Safari/ iOS
      auto_compaction: false, // as suggested online
    });
  }
  private clearDbLocal(): void {
    this.dbLocal = null;
  }
}

const instance = new PouchLoadService();
export default instance as PouchLoadService;
